/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useCallback, useEffect, useState } from "react";
import Icon from "react-icons-kit";
import { pencil } from "react-icons-kit/fa/pencil";
import { View, TextInput, TouchableOpacity } from "react-native";
import { useSelector } from "react-redux";

import {
  skaleBlue,
  skalePrimaryBlue,
  skaleRed,
  skaleWebTextLightGray,
  skaleWhite,
} from "config/UpduoColors";
import { ProfileImage, Button, Text, styles } from "UpduoLibrary";

import { callApi } from "config/UpduoService";
import { RootState } from "store/types";

import LockIcon from "@material-ui/icons/Lock";

import * as UpduoAnalytics from "config/UpduoAnalytics";

export default function AccountSettings({ onShowPhotoModal }) {
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [canEditTitle, setCanEditTitle] = useState(true);
  const [alertString, setAlertString] = useState("");
  const [alertColor, setAlertColor] = useState(skaleRed);

  useEffect(() => {
    _handleGetAccountSettings();
  }, []);

  const _handleSave = () => {
    UpduoAnalytics.logEvent("user-settings-account-save");
    callApi("postAccountSettings", { title: jobTitle });
    setAlertString("Your changes are saved!");
    setAlertColor("green");
  };

  const _handleGetAccountSettings = useCallback(() => {
    callApi("getAccountSettings", undefined, (responseJson) => {
      setFirstName(responseJson.first_name);
      setLastName(responseJson.last_name);
      setUserEmail(responseJson.primary_email);
      setJobTitle(responseJson.title);
      setCanEditTitle(responseJson.allow_title_edit);
    });
  }, [_handleSave]);

  const _handleShowUnsavedWarning = useCallback(() => {
    setAlertString("You have unsaved changes.");
    setAlertColor(skaleRed);
  }, []);

  return (
    <View style={{ flexDirection: "row", paddingTop: 20, width: 423 }}>
      <View
        style={{
          marginBottom: 20,
          flexDirection: "row",
        }}
      >
        <View style={{ marginBottom: 20, marginTop: 20 }}>
          <ProfileImage size={150} userObject={selfObject} />
          <TouchableOpacity
            style={{
              position: "absolute",
              bottom: 70,
              right: 10,
              width: 30,
              height: 30,
              borderRadius: 15,
              backgroundColor: skaleBlue,
              alignItems: "center",
              justifyContent: "center",
              paddingBottom: 3,
            }}
            onPress={() => {
              onShowPhotoModal();
            }}
          >
            <div
              style={{
                color: skaleWhite,
              }}
            >
              <Icon size={22} icon={pencil} />
            </div>
          </TouchableOpacity>
        </View>
      </View>

      <View style={{ flexDirection: "column", marginLeft: 40 }}>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            marginBottom: 20,
            alignItems: "center",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <TextInput
              placeholder={firstName}
              editable={false}
              style={[styles.settingsInput, { width: 186 }]}
            />
            <LockIcon
              style={{
                color: skaleWebTextLightGray,
                fontSize: "medium",
                position: "absolute",
                right: 12,
              }}
            />
          </View>
          <View
            style={{
              marginLeft: 16,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <TextInput
              placeholder={lastName}
              editable={false}
              style={[styles.settingsInput, { width: 221 }]}
            />
            <LockIcon
              style={{
                color: skaleWebTextLightGray,
                fontSize: "medium",
                position: "absolute",
                right: 12,
              }}
            />
          </View>
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <TextInput
            placeholder={userEmail}
            editable={false}
            style={[styles.settingsInput, { width: "100%" }]}
          />
          <LockIcon
            style={{
              color: skaleWebTextLightGray,
              fontSize: "medium",
              position: "absolute",
              right: 12,
            }}
          />
        </View>
        <View
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <TextInput
            placeholder={jobTitle ? jobTitle : "Title"}
            editable={canEditTitle}
            style={[styles.settingsInput, { width: "100%" }]}
            onChangeText={(newTitle) => {
              setJobTitle(newTitle);
              _handleShowUnsavedWarning();
            }}
          />
          {canEditTitle ? null : (
            <LockIcon
              style={{
                color: skaleWebTextLightGray,
                fontSize: "medium",
                position: "absolute",
                right: 12,
              }}
            />
          )}
        </View>
        <Button
          text={"SAVE CHANGES"}
          fontSize={16}
          height={48}
          color={skalePrimaryBlue}
          textColor={skaleWhite}
          width={"100%"}
          onPress={() => _handleSave()}
          disabled={!canEditTitle}
        />
        <Text
          style={{
            color: alertColor,
            marginVertical: 10,
          }}
        >
          {alertString}
        </Text>
      </View>
    </View>
  );
}
